import { useEffect, useState } from "react";
import api, { normalizeDecimal, DECIMAL_HINT, containerToBase, containerBase } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { StatusBadge } from "../components/Badges";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Plus, Trash2, PackageCheck } from "lucide-react";
import { toast } from "sonner";
import ShelfSelect from "../components/ShelfSelect";
import ItemCombobox from "../components/ItemCombobox";

export default function Purchase() {
  const [orders, setOrders] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ supplier_id: "", order_date: "", expected_date: "", notes: "" });
  const [lines, setLines] = useState([{ item_id: "", quantity: "", price: "" }]);
  const [receiveFor, setReceiveFor] = useState(null);
  const [receiveLines, setReceiveLines] = useState([]);

  const load = async () => setOrders((await api.get("/purchase")).data);
  useEffect(() => {
    load();
    api.get("/suppliers").then((r) => setSuppliers(r.data));
    api.get("/items").then((r) => setItems(r.data));
  }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        lines: lines.filter((l) => l.item_id && Number(l.quantity) > 0)
                    .map((l) => ({ item_id: l.item_id, quantity: Number(l.quantity), price: Number(l.price || 0) })),
      };
      if (payload.lines.length === 0) return toast.error("Mindestens 1 Position nötig");
      await api.post("/purchase", payload);
      toast.success("Bestellung erstellt");
      setOpen(false); setForm({ supplier_id: "", order_date: "", expected_date: "", notes: "" });
      setLines([{ item_id: "", quantity: "", price: "" }]);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
  };

  const openReceive = async (id) => {
    const { data } = await api.get(`/purchase/${id}`);
    setReceiveFor(id);
    setReceiveLines(data.lines.map((l) => {
      const it = items.find((i) => i.id === l.item_id);
      const isRaw = it?.type === "raw";
      return {
        item_id: l.item_id, item_name: l.item_name, item_unit: l.item_unit,
        quantity: l.quantity, charge_number: "", mhd: "", location: "",
        container_count: "",
        // Vorbelegung aus Artikel-Stammdaten
        container_size: isRaw ? (it?.container_size || "") : "",
        container_unit: isRaw ? (it?.container_unit || "kg") : "kg",
        container_label: isRaw ? (it?.container_label || "Sack") : "Sack",
      };
    }));
  };

  const isRawItem = (item_id) => (items.find((it) => it.id === item_id)?.type === "raw");
  const computedGrams = (l) => {
    const n = Number(l.container_count) || 0;
    const s = Number(l.container_size) || 0;
    if (!n || !s) return null;
    return n * containerToBase(1, l.container_unit) * s;
  };

  const submitReceive = async (e) => {
    e.preventDefault();
    try {
      const payload = receiveLines
        .filter((l) => l.mhd && Number(l.quantity) > 0)
        .map((l) => {
          const isRaw = isRawItem(l.item_id);
          const hasGebinde = isRaw && Number(l.container_count) > 0 && Number(l.container_size) > 0;
          const grams = hasGebinde ? computedGrams(l) : null;
          return {
            item_id: l.item_id,
            quantity: grams != null ? grams : Number(l.quantity),
            charge_number: l.charge_number || null,   // leer → Auto-Vergabe im Backend
            mhd: l.mhd,
            location: l.location || null,
            container_count: hasGebinde ? Number(l.container_count) : null,
            container_size: hasGebinde ? Number(l.container_size) : null,
            container_unit: hasGebinde ? l.container_unit : null,
            container_label: hasGebinde ? l.container_label : null,
          };
        });
      if (payload.length === 0) return toast.error("Bitte MHD und Menge ausfüllen");
      const { data } = await api.post(`/purchase/${receiveFor}/receive`, payload);
      const created = data?.received_batches || [];
      const first = created[0]?.charge_number;
      toast.success(created.length > 1
        ? `Wareneingang gebucht · ${created.length} Chargen (${first || "Auto"} …)`
        : `Ware verbucht · Charge ${first || "angelegt"}`);
      setReceiveFor(null); setReceiveLines([]); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
  };

  return (
    <div data-testid="page-purchase">
      <PageHeader
        eyebrow="Beschaffung"
        title="Einkauf"
        description="Bestellungen erfassen, Wareneingang mit Charge + MHD in einem Rutsch buchen."
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="btn-new-po" className="rounded-full bg-[var(--pop-yellow)] text-black hover:bg-[var(--pop-yellow-dark)] font-bold"><Plus size={16} className="mr-1" /> Neue Bestellung</Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-display text-2xl">Neue Bestellung</DialogTitle></DialogHeader>
              <form onSubmit={create} className="space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Lieferant *</Label>
                    <ItemCombobox
                      items={suppliers.map((s) => ({ id: s.id, sku: "", name: s.name, unit: s.city || "" }))}
                      value={form.supplier_id}
                      onChange={(v) => setForm({ ...form, supplier_id: v })}
                      placeholder="Lieferant wählen…"
                      testId="select-po-supplier"
                    />
                  </div>
                  <div><Label>Bestelldatum</Label><Input type="date" value={form.order_date} onChange={(e) => setForm({ ...form, order_date: e.target.value })} /></div>
                  <div><Label>Erwartet</Label><Input type="date" value={form.expected_date} onChange={(e) => setForm({ ...form, expected_date: e.target.value })} /></div>
                </div>
                <div className="border-t pt-4">
                  <div className="flex justify-between mb-2">
                    <Label className="overline">Positionen</Label>
                    <Button type="button" size="sm" variant="outline" onClick={() => setLines([...lines, { item_id: "", quantity: "", price: "" }])} className="rounded-full"><Plus size={14} /> Zeile</Button>
                  </div>
                  <div className="space-y-2">
                    {lines.map((l, idx) => (
                      <div key={idx} className="grid grid-cols-[1fr_120px_120px_40px] gap-2 items-center">
                        <ItemCombobox
                          items={items}
                          value={l.item_id}
                          onChange={(v) => { const c = [...lines]; c[idx].item_id = v; setLines(c); }}
                          placeholder="Artikel…"
                          testId={`select-po-line-${idx}`}
                        />
                        <Input type="number" step="any" placeholder={DECIMAL_HINT} value={l.quantity} onChange={(e) => { const c = [...lines]; c[idx].quantity = normalizeDecimal(e.target.value); setLines(c); }} />
                        <Input type="number" step="0.01" placeholder="Preis € (z.B. 1.85)" value={l.price} onChange={(e) => { const c = [...lines]; c[idx].price = normalizeDecimal(e.target.value); setLines(c); }} />
                        <button type="button" onClick={() => setLines(lines.filter((_, i) => i !== idx))} className="text-red-500"><Trash2 size={16} /></button>
                      </div>
                    ))}
                  </div>
                </div>
                <DialogFooter>
                  <Button type="submit" data-testid="btn-save-po" className="rounded-full bg-[var(--pop-yellow)] text-black hover:bg-[var(--pop-yellow-dark)] font-bold">Speichern</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="bg-white border border-[var(--border)] rounded-2xl overflow-hidden">
        <Table className="table-zebra">
          <TableHeader>
            <TableRow>
              <TableHead className="overline">Nr.</TableHead>
              <TableHead className="overline">Lieferant</TableHead>
              <TableHead className="overline">Erwartet</TableHead>
              <TableHead className="overline">Positionen</TableHead>
              <TableHead className="overline">Status</TableHead>
              <TableHead className="overline text-right">Aktion</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {orders.map((o) => (
              <TableRow key={o.id} data-testid={`po-row-${o.number}`}>
                <TableCell className="font-mono">{o.number}</TableCell>
                <TableCell>{o.supplier_name}</TableCell>
                <TableCell className="font-mono text-xs">{o.expected_date || "—"}</TableCell>
                <TableCell className="font-mono text-xs">{(o.lines || []).length}</TableCell>
                <TableCell><StatusBadge status={o.status} /></TableCell>
                <TableCell className="text-right">
                  {o.status !== "received" && (
                    <button onClick={() => openReceive(o.id)} data-testid={`btn-receive-${o.number}`} className="inline-flex items-center gap-1 text-xs font-semibold text-[var(--success)] hover:underline">
                      <PackageCheck size={14} /> Wareneingang
                    </button>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {orders.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-10 text-[var(--text-muted)]">Keine Bestellungen.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!receiveFor} onOpenChange={(o) => !o && setReceiveFor(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle className="font-display text-2xl">Wareneingang</DialogTitle></DialogHeader>
          <form onSubmit={submitReceive} className="space-y-3">
            <div className="overline">Chargen, MHD & Regal pro Position</div>
            <div className="text-xs text-[var(--text-muted)] -mt-1">
              Chargen-Nr. leer lassen → automatische Vergabe im Format <code className="font-mono">YYYY-MM-DD-NN</code> (fortlaufend pro Tag).
            </div>
            {receiveLines.map((l, idx) => {
              const isRaw = isRawItem(l.item_id);
              const grams = isRaw ? computedGrams(l) : null;
              return (
              <div key={idx} className="rounded-xl border border-[var(--border)] p-3 space-y-2" data-testid={`receive-row-${idx}`}>
                <div className="grid grid-cols-[1fr_90px_130px_130px_140px] gap-2 items-center">
                  <div className="text-sm font-semibold">{l.item_name}
                    {isRaw && <span className="ml-2 pop-pill bg-orange-100 text-orange-700 text-[10px] font-bold">ROHSTOFF</span>}
                  </div>
                  <Input type="number" step="any" placeholder={`Menge${isRaw ? " (g)" : ""}`}
                          data-testid={`input-receive-qty-${idx}`}
                          value={grams != null ? grams : l.quantity}
                          disabled={grams != null}
                          onChange={(e) => { const c = [...receiveLines]; c[idx].quantity = e.target.value; setReceiveLines(c); }} />
                  <Input placeholder="Auto (YYYY-MM-DD-NN)" data-testid={`input-receive-charge-${idx}`} value={l.charge_number} onChange={(e) => { const c = [...receiveLines]; c[idx].charge_number = e.target.value; setReceiveLines(c); }} />
                  <Input type="date" data-testid={`input-receive-mhd-${idx}`} value={l.mhd} onChange={(e) => { const c = [...receiveLines]; c[idx].mhd = e.target.value; setReceiveLines(c); }} />
                  <ShelfSelect
                    value={l.location}
                    onChange={(v) => { const c = [...receiveLines]; c[idx].location = v; setReceiveLines(c); }}
                    itemType={items.find((it) => it.id === l.item_id)?.type}
                    itemId={l.item_id}
                    quantity={Number(l.quantity) || 0}
                    placeholder="Regal wählen (opt.)"
                    testId={`input-receive-location-${idx}`}
                  />
                </div>
                {isRaw && (
                  <div className="grid grid-cols-[90px_120px_90px_1fr_auto] gap-2 items-center pt-1 border-t border-dashed" data-testid={`gebinde-row-${idx}`}>
                    <Input type="number" min="0" step="1" placeholder="Anzahl" data-testid={`input-gebinde-count-${idx}`}
                            value={l.container_count}
                            onChange={(e) => { const c = [...receiveLines]; c[idx].container_count = e.target.value; setReceiveLines(c); }} />
                    <Input type="number" min="0" step="any" placeholder="je Gebinde" data-testid={`input-gebinde-size-${idx}`}
                            value={l.container_size}
                            onChange={(e) => { const c = [...receiveLines]; c[idx].container_size = e.target.value; setReceiveLines(c); }} />
                    <Select value={l.container_unit} onValueChange={(v) => { const c = [...receiveLines]; c[idx].container_unit = v; setReceiveLines(c); }}>
                      <SelectTrigger data-testid={`select-gebinde-unit-${idx}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="kg">Kilogramm (kg)</SelectItem>
                        <SelectItem value="g">Gramm (g)</SelectItem>
                        <SelectItem value="l">Liter (l)</SelectItem>
                        <SelectItem value="ml">Milliliter (ml)</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select value={l.container_label} onValueChange={(v) => { const c = [...receiveLines]; c[idx].container_label = v; setReceiveLines(c); }}>
                      <SelectTrigger data-testid={`select-gebinde-label-${idx}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Sack">Sack</SelectItem>
                        <SelectItem value="Karton">Karton</SelectItem>
                        <SelectItem value="Eimer">Eimer</SelectItem>
                        <SelectItem value="Fass">Fass</SelectItem>
                        <SelectItem value="Palette">Palette</SelectItem>
                        <SelectItem value="Kiste">Kiste</SelectItem>
                        <SelectItem value="Flasche">Flasche</SelectItem>
                        <SelectItem value="Packung">Packung</SelectItem>
                        <SelectItem value="Kanister">Kanister</SelectItem>
                      </SelectContent>
                    </Select>
                    {grams != null ? (
                      <div className="text-xs font-mono font-bold text-[var(--pop-pink)] whitespace-nowrap"
                            data-testid={`gebinde-total-${idx}`}>
                        = {grams.toLocaleString("de-DE")} {containerBase(l.container_unit)}
                      </div>
                    ) : (
                      <div className="text-xs text-[var(--text-muted)] italic">Gebinde-Angabe optional</div>
                    )}
                  </div>
                )}
              </div>
              );
            })}
            <div className="text-xs text-[var(--text-muted)]">Bei Rohstoffen: Anzahl Gebinde × Gewicht → wird automatisch in Gramm umgerechnet und als Notiz zur Charge gespeichert.</div>
            <DialogFooter>
              <Button type="submit" data-testid="btn-submit-receive" className="rounded-full bg-[var(--pop-yellow)] text-black hover:bg-[var(--pop-yellow-dark)] font-bold">Wareneingang buchen</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
